import React from "react";
import { useSelector } from "react-redux";
import { array, bool, string } from "prop-types";
import get from "lodash/get";
import { Link } from "@quintype/components";
import { DfpComponent } from "../../ads/dfp-component";
import { HeroImage } from "../../atoms/HeroImage";
import { Label } from "../../atoms/Label";
import { PublishAndReadTime } from "../../atoms/PublishAndReadTime";
import { getStoryHeadline, getStoryLabel, isExternalStory } from "../../utils/story";
import { MainStory } from "./MainStory";
import { OtherStory } from "./OtherStory";
import Headline from "../../atoms/Headline";

import "./three-col-nineteen-stories-var-two.m.css";

const SecondDivStory = ({ story, showReadTime, showTimestamp, showBorder, sectionSlug }) => {
  const label = getStoryLabel(story);
  const imageUrl =
    get(story, ["hero-image-s3-key"], "") ||
    get(story, ["alternative", "home", "default", "hero-image", "hero-image-s3-key"], "");

  const externalStoryUrl = isExternalStory(story);
  const href = externalStoryUrl || `/${story?.slug}`;
  const target = externalStoryUrl ? "_blank" : "_self";
  const headline = getStoryHeadline(story);
  return (
    <Link href={href} target={target} key={story.id}>
      <div key={story?.id} styleName={`second-set-story-card ${showBorder ? "" : "no-border"}`}>
        <HeroImage
          story={story}
          headline={story?.headline}
          aspectRatio={[[4, 3]]}
          iconSizes={{ height: 24, width: 24 }}
          imageUrl={imageUrl}
        />
        <div styleName="card-details">
          {label && <Label story={story} />}
          {headline && <Headline styleName="headline" headline={headline} sectionSlug={sectionSlug} />}
          <PublishAndReadTime showReadTime={showReadTime} showTimestamp={showTimestamp} story={story} />
        </div>
      </div>
    </Link>
  );
};

export const PrimaryColumn = ({
  stories,
  isMobile,
  showHeroImage,
  showReadTime,
  showTimestamp,
  showStorySeparator,
  sectionSlug,
}) => {
  const [firstStory, ...remainingStories] = stories;

  return (
    <div styleName="first-column">
      <MainStory
        story={firstStory}
        showBorder={remainingStories?.length > 0 && showStorySeparator}
        showHeroImage={showHeroImage}
        showReadTime={showReadTime}
        showTimestamp={showTimestamp}
        sectionSlug={sectionSlug}
      />
      <div styleName="first-col-second-set">
        {remainingStories.map((story, index) =>
          isMobile ? (
            <div key={story?.id} styleName="second-set-mobile">
              <OtherStory
                story={story}
                showReadTime={showReadTime}
                showTimestamp={showTimestamp}
                showThumbnail={true}
                showBorder={showStorySeparator}
                defaultFallback={true}
                sectionSlug={sectionSlug}
              />
            </div>
          ) : (
            <div styleName={`new-set ${showStorySeparator ? "" : "no-border"}`}>
              <SecondDivStory
                story={story}
                showReadTime={showReadTime}
                showTimestamp={showTimestamp}
                showBorder={showStorySeparator}
                sectionSlug={sectionSlug}
              />
            </div>
          )
        )}
      </div>
    </div>
  );
};

PrimaryColumn.propTypes = {
  stories: array,
  showHeroImage: bool,
  isMobile: bool,
  showReadTime: bool,
  showTimestamp: bool,
  showStorySeparator: bool,
  sectionSlug: string,
};

export const SecondaryColumn = ({
  stories,
  showAd,
  rowAdUnit,
  showReadTime,
  showTimestamp,
  showStorySeparator,
  collection,
  isMobile,
  sectionSlug,
}) => {
  const [firstStory, ...remainingStories] = stories;
  const label = getStoryLabel(firstStory);
  const imageUrl =
    get(firstStory, ["hero-image-s3-key"], "") ||
    get(firstStory, ["alternative", "home", "default", "hero-image", "hero-image-s3-key"], "");
  const rowAdConfig = useSelector((state) =>
    get(state, ["qt", "config", "ads-config", "slots", "row_ad_story_300x250"], {})
  );
  const rowAd = rowAdUnit?.split("/").slice(-1)[0];
  const rowAdDivId = useSelector((state) => get(state, ["qt", "config", "ads-config", "ad_div_mapping", rowAd], ""));
  const pageType = useSelector((state) => get(state, ["qt", "pageType"], null));

  const externalStoryUrl = isExternalStory(firstStory);
  const href = externalStoryUrl || `/${firstStory?.slug}`;
  const target = externalStoryUrl ? "_blank" : "_self";
  return (
    <div styleName="second-column">
      {showAd && (
        <div styleName="adSlot">
          <DfpComponent adStyleName="three-col-nineteen-ad-slot-size-300x250" id={rowAdDivId} />
        </div>
      )}
      {firstStory && (
        <>
          {isMobile ? (
            <div styleName="second-set-mobile">
              <OtherStory
                story={firstStory}
                showReadTime={showReadTime}
                showTimestamp={showTimestamp}
                showThumbnail={true}
                showBorder={showStorySeparator}
                defaultFallback={true}
                sectionSlug={sectionSlug}
              />
            </div>
          ) : (
            <div styleName={`second-column-first-story ${showStorySeparator ? "" : "no-border"}`}>
              <SecondDivStory
                story={firstStory}
                showReadTime={showReadTime}
                showTimestamp={showTimestamp}
                showBorder={showStorySeparator}
                sectionSlug={sectionSlug}
              />
            </div>
          )}
        </>
      )}
      {remainingStories?.map((story, index) => (
        <div key={story.id}>
          <OtherStory
            story={story}
            showReadTime={showReadTime}
            showTimestamp={showTimestamp}
            showThumbnail={false}
            showBorder={showStorySeparator}
            sectionSlug={sectionSlug}
          />
        </div>
      ))}
    </div>
  );
};

SecondaryColumn.propTypes = {
  stories: array,
  showAd: bool,
  adSlotLabel: string,
  showReadTime: bool,
  showTimestamp: bool,
  showStorySeparator: bool,
  sectionSlug: string,
};

export const TertiaryColumn = ({
  stories,
  photosLabel,
  showReadTime,
  showTimestamp,
  showStorySeparator,
  showAd,
  rowAdUnit,
  collection,
  deviceType,
  sectionSlug,
}) => {
  const rowAdConfig = useSelector((state) =>
    get(state, ["qt", "config", "ads-config", "slots", "row_ad_story_300x250"], {})
  );
  const rowAd = rowAdUnit?.split("/").slice(-1)[0];
  const rowAdDivId = useSelector((state) => get(state, ["qt", "config", "ads-config", "ad_div_mapping", rowAd], ""));
  const pageType = useSelector((state) => get(state, ["qt", "pageType"], null));
  return (
    <div styleName="third-column">
      {showAd && (
        <div styleName="adSlot">
          <DfpComponent adStyleName="three-col-nineteen-ad-slot-size-300x250" id={rowAdDivId} />
        </div>
      )}
      <div styleName="third-column-stories">
        <div styleName="left-stories">
          {stories.slice(0, 3).map((story, index) => (
            <div key={story.id}>
              <OtherStory
                story={story}
                showReadTime={showReadTime}
                showTimestamp={showTimestamp}
                showThumbnail={false}
                showBorder={showStorySeparator && (deviceType !== "tablet" || index !== 2)}
                sectionSlug={sectionSlug}
              />
            </div>
          ))}
        </div>
        {stories.length >= 4 && (
          <div>
            <div>
              <OtherStory
                story={stories[3]}
                showReadTime={showReadTime}
                showTimestamp={showTimestamp}
                showThumbnail={false}
                showBorder={showStorySeparator && false}
                sectionSlug={sectionSlug}
              />
            </div>
            {stories.length >= 5 && (
              <>
                <div styleName="text-with-line">{photosLabel}</div>
                <div styleName="photos-container">
                  {stories.slice(4, 6).map((story) => {
                    const label = getStoryLabel(story);
                    const imageUrl =
                      get(story, ["hero-image-s3-key"], "") ||
                      get(story, ["alternative", "home", "default", "hero-image", "hero-image-s3-key"], "");

                    const externalStoryUrl = isExternalStory(story);
                    const href = externalStoryUrl || `/${story?.slug}`;
                    const target = externalStoryUrl ? "_blank" : "_self";
                    const headline = getStoryHeadline(story);
                    return (
                      <Link href={href} target={target} key={story.id}>
                        <div styleName="photo-story" style={{ border: showStorySeparator || "none" }}>
                          <HeroImage
                            story={story}
                            headline={story.headline}
                            aspectRatio={[[4, 3]]}
                            iconSizes={{ height: 24, width: 24 }}
                            imageUrl={imageUrl}
                          />
                          {label && <Label story={story} />}
                          {headline && <Headline styleName="headline" headline={headline} sectionSlug={sectionSlug} />}
                          <PublishAndReadTime showReadTime={showReadTime} showTimestamp={showTimestamp} story={story} />
                        </div>
                      </Link>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

TertiaryColumn.propTypes = {
  stories: array,
  photosLabel: string,
  showAd: bool,
  adSlotLabel: string,
  deviceType: string,
  showReadTime: bool,
  showTimestamp: bool,
  showStorySeparator: bool,
  sectionSlug: string,
};
